import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Search, ChevronDown, Upload, X, Save, Plus, RefreshCw } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useProduct,
  useProductCategoryTree,
  useProductsByCategory,
  useSubcategories,
  type ProductDetail,
  type ProductSummary,
} from "@/lib/products";
import { quotesApi } from "@/lib/quotes";
import { jobsApi } from "@/lib/jobs";
import { useRankedTemplates } from "@/lib/bom-templates";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { SelectSubcategoryDialog } from "./SelectSubcategoryDialog";

// ---------------------------------------------------------------------------
// ProductPicker — embedded picker matching `Select Product.png`.
//
// Layout (matches the spec wireframe):
//
//   ┌──────────────────────────┬──────────────────────────┐
//   │ Select Product           │ Product Description      │
//   │  (category tree + list)  │  (selected product       │
//   │                          │   name + description)    │
//   └──────────────────────────┴──────────────────────────┘
//   [Cancel]  [Add to Quote / Add to Job]
//
// State: local-only (no global store). The picker owns category drill-down,
// product selection, the template-choice step, and the in-flight "Add" mutation.
//
// Modes:
//   onPicked mode (NewQuotePage): if `onPicked` is provided, Add calls
//   `onPicked(product)` + `onAdded()` — NO template step, NO API call.
//   Persisted context mode (QuoteDetailPage / JobDetailsTab): Add opens a
//   template-choice step, then calls itemsFromProduct on the chosen context.
// ---------------------------------------------------------------------------

export type ProductPickerContext =
  | { kind: "quote"; id: number }
  | { kind: "job"; id: number };

export interface ProductPickerProps {
  /** What we are adding into (a quote, or a job's BOM). */
  context: ProductPickerContext;
  /** Called when user clicks Cancel. Parent typically unmounts the picker. */
  onCancel: () => void;
  /** Called after a successful add. Parent typically unmounts + invalidates. */
  onAdded: () => void;
  /**
   * When provided, Add calls this with the selected product instead of POSTing
   * to the quote (used where no quote exists yet, e.g. the New Quote form).
   */
  onPicked?: (product: ProductDetail) => void;
  /**
   * Called after a successful itemsFromProduct call (persisted-context mode only).
   * Receives the newly created BOM id so the parent can navigate to it.
   */
  onCreatedBom?: (bomId: number) => void;
}

export function ProductPicker({ context, onCancel, onAdded, onPicked, onCreatedBom }: ProductPickerProps) {
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  // When true, we've moved past product selection and are showing the template
  // choice step (only in persisted-context mode — never when onPicked is set).
  const [choosingTemplate, setChoosingTemplate] = useState(false);
  // Task 9: subcategory drilldown — when an opened category has children, we
  // overlay `SelectSubcategoryDialog` until the user picks one or closes it.
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  const [subcategoryParentId, setSubcategoryParentId] = useState<number | null>(
    null,
  );

  const { has } = usePermissions();

  const categoriesQ = useProductCategoryTree();
  const productsQ = useProductsByCategory(categoryId);
  const productQ = useProduct(selectedProductId);
  const subcategoriesQ = useSubcategories(subcategoryParentId);
  const templatesQ = useRankedTemplates(choosingTemplate ? selectedProductId : null);

  const product = productQ.data ?? null;

  // When a parent category is clicked, open the modal-over-embedded
  // subcategory dialog instead of populating the product list directly.
  // When the dialog reports a pick, the parent's id is replaced with the
  // child's id and the product list reloads against that subcategory.
  function handleCategoryClick(id: number, hasSubcategories: boolean) {
    if (hasSubcategories) {
      setSubcategoryParentId(id);
      setSubcategoryDialogOpen(true);
      return;
    }
    setCategoryId(id);
    setSelectedProductId(null);
  }

  function handleSubcategorySelected(subcategoryId: number) {
    setCategoryId(subcategoryId);
    setSelectedProductId(null);
  }

  function handleProductClick(p: ProductSummary) {
    setSelectedProductId(p.productId);
  }

  function handleAdd() {
    if (!product) return;
    // onPicked mode (NewQuotePage): delegate immediately, no template step.
    if (onPicked) {
      onPicked(product);
      onAdded();
      return;
    }
    // Persisted-context mode: show template choice step.
    setChoosingTemplate(true);
  }

  async function submit(bomTemplateId: number | null) {
    if (!product) return;
    setAdding(true);
    try {
      let bomId: number;
      if (context.kind === "quote") {
        const res = await quotesApi.itemsFromProduct(context.id, {
          productId: product.productId,
          bomTemplateId,
          quantity: 1,
        });
        bomId = res.bomId;
        await qc.invalidateQueries({ queryKey: ["boms", "Quote", context.id] });
        await qc.invalidateQueries({ queryKey: ["quote", context.id] });
        await qc.invalidateQueries({ queryKey: ["quote-items", context.id] });
      } else {
        const res = await jobsApi.itemsFromProduct(context.id, {
          productId: product.productId,
          bomTemplateId,
          quantity: 1,
        });
        bomId = res.bomId;
        await qc.invalidateQueries({ queryKey: ["boms", "Job", context.id] });
        await qc.invalidateQueries({ queryKey: ["job", context.id] });
        await qc.invalidateQueries({ queryKey: ["jobs", context.id, "items"] });
      }
      toast.success(`Added ${product.productName}.`);
      onCreatedBom?.(bomId);
      onAdded();
    } catch (err) {
      toast.error(
        `Add failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setAdding(false);
    }
  }

  // -----------------------------------------------------------------------
  // Empty-catalog notice — top-level guard.
  // -----------------------------------------------------------------------
  const catalogEmpty =
    !categoriesQ.isLoading && !categoriesQ.isError && (categoriesQ.data ?? []).length === 0;
  const catalogError = categoriesQ.isError;

  // -----------------------------------------------------------------------
  // Template-choice step (persisted-context mode only).
  // -----------------------------------------------------------------------
  if (choosingTemplate && product) {
    const templates = templatesQ.data ?? [];
    // Group by displayGroup for labelled sections.
    const groups = templates.reduce<Record<string, typeof templates>>((acc, t) => {
      (acc[t.displayGroup] ??= []).push(t);
      return acc;
    }, {});
    const groupKeys = Object.keys(groups);

    return (
      <div className="space-y-4" role="region" aria-labelledby="template-step-heading">
        <header className="flex items-center justify-between">
          <h2 id="template-step-heading" className="text-lg font-semibold tracking-tight">
            Choose a Template
          </h2>
          <p className="text-xs text-muted-foreground">
            {product.productName}
          </p>
        </header>

        <Card>
          <CardContent className="pt-4 space-y-3">
            {templatesQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading templates…</p>
            ) : (
              <>
                {groupKeys.map((group) => (
                  <div key={group}>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group}
                    </p>
                    <ul className="space-y-1">
                      {(groups[group] ?? []).map((t) => (
                        <li key={t.bomTemplateId}>
                          <button
                            type="button"
                            disabled={adding}
                            onClick={() => void submit(t.bomTemplateId)}
                            className="w-full rounded px-3 py-2 text-left text-sm hover:bg-muted"
                          >
                            <span className="font-medium">{t.bomName}</span>
                            {t.bomDesc && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {t.bomDesc}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="border-t border-border pt-3">
                  <button
                    type="button"
                    disabled={adding}
                    onClick={() => void submit(null)}
                    className="w-full rounded px-3 py-2 text-left text-sm font-medium hover:bg-muted"
                  >
                    Start blank
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            type="button"
            onClick={() => setChoosingTemplate(false)}
            disabled={adding}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-4" role="region" aria-labelledby="product-picker-heading">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-4">
        {/* Select Product Card */}
        <Card className="border border-slate-400 rounded-lg bg-white shadow-sm overflow-hidden flex flex-col">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-lg font-bold text-[#1a2f4c]">
              Select Product
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3 flex flex-col flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input placeholder="Search Product (Banners, Stands)" className="pl-9 bg-slate-100 border-0" />
            </div>
            
            <h4 className="text-sm font-bold text-[#1a2f4c] mt-2 mb-2">Product Selection</h4>
            
            {categoriesQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading categories…</p>
            ) : catalogError ? (
              <p className="text-sm text-destructive">Could not load the catalogue.</p>
            ) : catalogEmpty ? (
              <div className="space-y-2 rounded border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                <p>No product categories found. Add catalogue data to start picking products.</p>
                <div className="flex flex-wrap gap-2">
                  {has("product-categories:write") && (
                    <a className="text-primary underline" href="/admin/catalog/categories" target="_blank" rel="noopener noreferrer">
                      Add product category
                    </a>
                  )}
                  {has("products:write") && (
                    <a className="text-primary underline" href="/admin/catalog/products" target="_blank" rel="noopener noreferrer">
                      Add product
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <ul aria-label="Product categories" className="space-y-2 text-sm text-slate-700 font-medium">
                {(categoriesQ.data ?? []).map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => handleCategoryClick(c.id, c.hasSubcategories)}
                      className={`w-full text-left hover:text-[#1a2f4c] transition-colors ${
                        categoryId === c.id ? "text-[#1a2f4c] font-bold" : ""
                      }`}
                      aria-pressed={categoryId === c.id}
                    >
                      {c.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {categoryId != null && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-bold text-[#1a2f4c] mb-2">Products</h4>
                {productsQ.isLoading ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : (productsQ.data ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No products found.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {(productsQ.data ?? []).map((p) => (
                      <li key={p.productId}>
                        <button
                          type="button"
                          onClick={() => handleProductClick(p)}
                          className={`w-full rounded px-2 py-1.5 text-left hover:bg-slate-100 transition-colors ${
                            selectedProductId === p.productId
                              ? "bg-slate-100 font-semibold text-[#1a2f4c]"
                              : "text-slate-700"
                          }`}
                          aria-pressed={selectedProductId === p.productId}
                        >
                          {p.productName}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Product Description Card */}
        <Card className="border border-slate-400 rounded-lg bg-white shadow-sm overflow-hidden flex flex-col">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-lg font-bold text-[#1a2f4c]">
              Product Description
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="grid grid-cols-[90px_1fr] items-center gap-3">
              <Label className="text-sm text-[#1a2f4c]">Product:</Label>
              <div className={`text-sm px-3 py-1.5 rounded-md ${product ? "bg-slate-100 text-[#1a2f4c] font-medium" : "bg-transparent text-slate-400 italic"}`}>
                {product ? product.productName : "No product selected"}
              </div>
            </div>
            
            <div className="grid grid-cols-[90px_1fr] items-start gap-3">
              <Label className="text-sm text-[#1a2f4c] mt-2">Graphic Size:</Label>
              <div className="space-y-2">
                <Select disabled={!product}>
                  <SelectTrigger className="bg-slate-100 border-0 text-slate-700"><SelectValue placeholder="Select width..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="600">600mm</SelectItem>
                    <SelectItem value="850">850mm</SelectItem>
                    <SelectItem value="1000">1000mm</SelectItem>
                    <SelectItem value="1200">1200mm</SelectItem>
                    <SelectItem value="custom">Custom...</SelectItem>
                  </SelectContent>
                </Select>
                <Select disabled={!product}>
                  <SelectTrigger className="bg-slate-100 border-0 text-slate-700"><SelectValue placeholder="Select height..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1600">1600mm</SelectItem>
                    <SelectItem value="2000">2000mm</SelectItem>
                    <SelectItem value="2200">2200mm</SelectItem>
                    <SelectItem value="custom">Custom...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-[90px_1fr] items-center gap-3">
              <Label className="text-sm text-[#1a2f4c]">Quantity:</Label>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-md text-sm text-slate-700 opacity-50"><span className="font-semibold">—</span> <ChevronDown className="w-4 h-4 text-slate-400"/></div>
              </div>
            </div>

            <div className="grid grid-cols-[90px_1fr] items-center gap-3">
              <Label className="text-sm text-[#1a2f4c]">Artwork:</Label>
              <Button variant="outline" disabled={!product} className="border-[#1a2f4c] text-[#1a2f4c] rounded-full px-3 h-7 text-xs font-semibold flex gap-1.5 w-fit">
                <Upload className="w-3 h-3" />
                Upload
              </Button>
            </div>

            <div className="pt-2">
              <h4 className="text-sm text-[#1a2f4c] mb-1">Description:</h4>
              <p className={`text-sm ${product?.description ? "text-[#1a2f4c]" : "text-slate-400 italic"}`}>
                {product?.description || "No description available."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quantity Total Card */}
      <Card className="border border-slate-400 rounded-lg bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead>
              <tr className="bg-slate-100 text-[#1a2f4c]">
                <th className="text-left font-bold p-4">Quantity Total</th>
                <th className="font-bold p-4 text-slate-400 font-normal">—</th>
                <th className="font-bold p-4 text-slate-400 font-normal">—</th>
                <th className="font-bold p-4 text-slate-400 font-normal">—</th>
                <th className="font-bold p-4 pr-6 text-slate-400 font-normal">—</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              <tr>
                <td colSpan={5} className="text-left font-bold text-[#1a2f4c] pt-4 pb-2 px-4">Buy/Production Price</td>
              </tr>
              <tr>
                <td className="text-left px-4 py-1">Print Cost</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1 pr-6">—</td>
              </tr>
              <tr>
                <td className="text-left px-4 py-1">Price per Piece</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1 pr-6">—</td>
              </tr>
              <tr className="font-bold text-[#1a2f4c]">
                <td className="text-left px-4 pt-4 pb-1">Total excl GST</td>
                <td className="px-4 pt-4 pb-1">—</td>
                <td className="px-4 pt-4 pb-1">—</td>
                <td className="px-4 pt-4 pb-1">—</td>
                <td className="px-4 pt-4 pb-1 pr-6">—</td>
              </tr>
              <tr>
                <td className="text-left px-4 py-1">GST</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1 pr-6">—</td>
              </tr>
              <tr className="font-bold text-[#1a2f4c]">
                <td className="text-left px-4 pb-3 pt-1">Total</td>
                <td className="px-4 pb-3 pt-1">—</td>
                <td className="px-4 pb-3 pt-1">—</td>
                <td className="px-4 pb-3 pt-1">—</td>
                <td className="px-4 pb-3 pt-1 pr-6">—</td>
              </tr>
              
              <tr>
                <td colSpan={5} className="text-left font-bold text-[#1a2f4c] pt-4 pb-2 px-4 border-t border-slate-100">Suggested Sell Price</td>
              </tr>
              <tr>
                <td className="text-left px-4 py-1">Price per Piece</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1 pr-6">—</td>
              </tr>
              <tr>
                <td className="text-left px-4 py-1">Print Cost</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1 pr-6">—</td>
              </tr>
              <tr className="font-bold text-[#1a2f4c]">
                <td className="text-left px-4 pt-3 pb-1">Total excl GST</td>
                <td className="px-4 pt-3 pb-1">—</td>
                <td className="px-4 pt-3 pb-1">—</td>
                <td className="px-4 pt-3 pb-1">—</td>
                <td className="px-4 pt-3 pb-1 pr-6">—</td>
              </tr>
              <tr>
                <td className="text-left px-4 py-1">GST</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1">—</td>
                <td className="px-4 py-1 pr-6">—</td>
              </tr>
              <tr className="font-bold text-[#1a2f4c]">
                <td className="text-left px-4 pb-3 pt-1">Total</td>
                <td className="px-4 pb-3 pt-1">—</td>
                <td className="px-4 pb-3 pt-1">—</td>
                <td className="px-4 pb-3 pt-1">—</td>
                <td className="px-4 pb-3 pt-1 pr-6">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Footer actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-full border-slate-400 text-[#1a2f4c] font-semibold" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button variant="outline" className="rounded-full border-slate-400 text-[#1a2f4c] font-semibold">
            <Save className="w-4 h-4 mr-2" /> Save Quote
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-full border-slate-400 text-[#1a2f4c] font-semibold" onClick={() => handleAdd()} disabled={adding}>
            <Plus className="w-4 h-4 mr-2" /> Add Product
          </Button>
          <Button variant="outline" className="rounded-full border-slate-400 text-[#1a2f4c] font-semibold">
            <RefreshCw className="w-4 h-4 mr-2" /> Calculate
          </Button>
        </div>
      </div>
    </div>

    {/* ------------------- Subcategory drilldown dialog --------------------- */}
    <SelectSubcategoryDialog
      open={subcategoryDialogOpen}
      onOpenChange={setSubcategoryDialogOpen}
      subcategories={subcategoriesQ.data ?? []}
      loading={subcategoriesQ.isLoading}
      onSelect={handleSubcategorySelected}
    />
    </>
  );
}
